import {
  LOAN_PALIERS_MIN,
  LoanComputationModel,
  LoanInsuranceQuotite,
  LoanInsuranceType,
  LoanPalierFormValue,
  LoanPalierIssue,
  LoanType,
} from './loan-palier.model';
import {
  isLoanDiffere,
  paliersFormToApi,
  toFiniteNumber,
} from './loan-palier.utils';

const PERCENT_DIVISOR = 100;

export interface LoanComputationFormValue {
  type?: string;
  amountBorrowed?: unknown;
  amountFee?: unknown;
  interestRate?: unknown;
  insuranceRate?: unknown;
  insuranceType?: unknown;
  insuranceQuotite?: unknown;
  durationTotalInMonth?: unknown;
  startDate?: unknown;
  paliers?: LoanPalierFormValue[];
}

export function toIsoDateString(value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const date = new Date(value as any);

  if (isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString().split('.')[0] + 'Z';
}

function toPercentFraction(value: unknown): number | null {
  const parsed = toFiniteNumber(value);

  return parsed === null ? null : parsed / PERCENT_DIVISOR;
}

function normalizeInsuranceQuotite(
  value: unknown,
): LoanInsuranceQuotite[] | undefined {
  if (!Array.isArray(value)) {
    const singleQuotite = toPercentFraction(value);

    return singleQuotite === null ? undefined : [{ quotite: singleQuotite }];
  }

  const normalized = value
    .filter(item => {
      const ownerId = item?.ownerId;

      return ownerId !== null && ownerId !== undefined && ownerId !== '';
    })
    .map(item => ({
      ownerId: Number(item.ownerId),
      quotite: toPercentFraction(item?.quotite) ?? 0,
    }));

  return normalized.length ? normalized : undefined;
}

export function buildLoanComputationModel(
  formValue: LoanComputationFormValue,
  fallbackStartDate?: unknown,
): LoanComputationModel {
  return {
    type: ((formValue?.type as LoanType) || 'FIX') as LoanType,
    amountBorrowed: toFiniteNumber(formValue?.amountBorrowed) ?? undefined,
    amountFee: toFiniteNumber(formValue?.amountFee) ?? undefined,
    interestRate: toPercentFraction(formValue?.interestRate) ?? undefined,
    insuranceRate: toPercentFraction(formValue?.insuranceRate) ?? 0,
    insuranceType: ((formValue?.insuranceType as LoanInsuranceType) ||
      'AMOUNT_BORROWED') as LoanInsuranceType,
    insuranceQuotite: normalizeInsuranceQuotite(formValue?.insuranceQuotite),
    durationTotalInMonth:
      toFiniteNumber(formValue?.durationTotalInMonth) ?? undefined,
    startDate:
      toIsoDateString(formValue?.startDate) ??
      toIsoDateString(fallbackStartDate) ??
      undefined,
    paliers: paliersFormToApi(formValue?.paliers) ?? undefined,
  };
}

export function missingLoanComputationFields(
  model: LoanComputationModel,
): string[] {
  const missing: string[] = [];

  if (!model?.startDate) {
    missing.push('la date de début du crédit');
  }
  if (!((model?.amountBorrowed ?? 0) > 0)) {
    missing.push('le montant emprunté');
  }
  if (model?.interestRate === null || model?.interestRate === undefined) {
    missing.push("le taux d'intérêt");
  }
  if (!((model?.durationTotalInMonth ?? 0) > 0)) {
    missing.push('la durée totale du crédit');
  }

  return missing;
}

export function loanPreviewBlockingReasons(
  model: LoanComputationModel,
  palierIssues: LoanPalierIssue[],
): string[] {
  return [
    ...missingLoanComputationFields(model).map(field => `Renseignez ${field}`),
    ...palierIssues.map(issue => issue.message),
  ];
}

export function isLoanComputationReady(model: LoanComputationModel): boolean {
  const paliers = model?.paliers;

  if (!Array.isArray(paliers) || paliers.length < LOAN_PALIERS_MIN) {
    return false;
  }

  if (!model.startDate) {
    return false;
  }

  const amountBorrowed = model.amountBorrowed ?? 0;

  if (!(amountBorrowed > 0)) {
    return false;
  }

  if (model.interestRate === null || model.interestRate === undefined) {
    return false;
  }

  const totalDuration = model.durationTotalInMonth ?? 0;

  if (!(totalDuration > 0)) {
    return false;
  }

  let durationsSum = 0;

  for (let index = 0; index < paliers.length; index++) {
    const palier = paliers[index];
    const palierDuration = palier?.durationInMonth ?? 0;

    if (!(palierDuration > 0) || !Number.isInteger(palierDuration)) {
      return false;
    }

    durationsSum += palierDuration;

    const isLast = index === paliers.length - 1;
    const monthlyPayment = palier.monthlyPayment ?? 0;

    if (!isLast && !isLoanDiffere(palier.differe) && !(monthlyPayment > 0)) {
      return false;
    }
  }

  return durationsSum === totalDuration;
}
